/**
 * `/progress` — §12.
 */
import { useMemo, useRef, useState } from 'react'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import {
  DAY_MS,
  GRADE_QUALITIES,
  TOTAL_PAGES,
  adherence,
  backupFilename,
  dayStamp,
  dueCards,
  juzPageRange,
  parseBackup,
  serialiseBackup,
  tierOf,
  type Meta,
} from '@mushaf/corpus'
import { GRADE_COPY, TIER_LABELS } from '../content/srs'
import { IMPORT_FAILURE_MESSAGES, importSuccessMessage } from '../content/backup'
import { SecondaryShell } from '../components/SecondaryShell'
import { WardPanel, describeScope } from '../components/ward/WardPanel'
import { selectActiveProgram, useLibrary } from '../stores/library'
import { useMeta } from '../lib/queries'
import { useNow } from '../lib/useNow'
import { Button, Chip, SectionTitle, linkClass } from '../components/ui'
import { IconDownload, IconUpload } from '../components/ui/icons'
import { Link } from '@tanstack/react-router'

export const Route = createFileRoute('/progress')({ component: ProgressPage })

function ProgressPage() {
  const { data: meta } = useMeta()
  const pages = useLibrary((s) => s.pages)
  const srs = useLibrary((s) => s.srs)
  const program = useLibrary(selectActiveProgram)

  const now = useNow()
  const read = Object.values(pages).filter((p) => p.read).length
  const memorised = Object.values(pages).filter((p) => p.memorised).length
  const due = useMemo(() => dueCards(Object.values(srs), now), [srs, now])
  const streak = useMemo(() => computeStreak(Object.values(pages), now), [pages, now])

  return (
    <SecondaryShell title="التقدّم">
      <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
        <Stat label="صفحات مقروءة" value={`${read} / ${TOTAL_PAGES}`} ratio={read / TOTAL_PAGES} />
        <Stat
          label="صفحات محفوظة"
          value={`${memorised} / ${TOTAL_PAGES}`}
          ratio={memorised / TOTAL_PAGES}
        />
        <Stat
          label="أيام متتالية"
          value={String(streak.current)}
          sub={`الأطول ${streak.longest}`}
        />
        <Stat label="مستحقّ اليوم" value={String(due.length)} sub={`من ${memorised}`} />
      </div>

      {program ? (
        <WardPanel meta={meta} />
      ) : (
        <section className="grid gap-2 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4">
          <p className="text-sm leading-7">
            لا برنامج مُفعَّل. البرنامج هو ما يجيب سؤال «ماذا أراجع اليوم؟» بدل أن يُترك للاجتهاد.
          </p>
          <Link to="/programs" className={linkClass('gold', 'w-fit text-xs')}>
            اختر برنامجًا
          </Link>
        </section>
      )}

      {program && meta && <Projection meta={meta} pages={pages} now={now} />}

      <DueList due={due} />

      <JuzGrid meta={meta} pages={pages} />

      <Backup />
    </SecondaryShell>
  )
}

function Stat({
  label,
  value,
  sub,
  ratio,
}: {
  label: string
  value: string
  sub?: string
  ratio?: number
}) {
  return (
    <div className="grid gap-1 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-3">
      <span className="text-[11px] text-[var(--ink-faint)]">{label}</span>
      {/* L22: `0 / 604` must never render as `604 / 0`, which reads as having
          finished 604 of nothing (T64). */}
      <span dir="ltr" className="text-lg font-semibold">
        {value}
      </span>
      {sub && (
        <span dir="ltr" className="text-[11px] text-[var(--ink-faint)]">
          {sub}
        </span>
      )}
      {ratio !== undefined && (
        <span className="h-1 overflow-hidden rounded-full bg-[var(--surface-2)]">
          <span
            className="block h-full bg-[var(--gold)]"
            style={{ width: `${Math.round(ratio * 100)}%` }}
          />
        </span>
      )}
    </div>
  )
}

function Projection({
  meta,
  pages,
  now,
}: {
  meta: Meta
  pages: Record<number, { page: number; readOn: number | null }>
  now: number
}) {
  const program = useLibrary(selectActiveProgram)!
  const a = adherence(program, meta, now)

  return (
    <section className="grid gap-2 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4">
      <SectionTitle>{program.name}</SectionTitle>
      <p className="text-xs text-[var(--ink-faint)]">{describeScope(program.scope)}</p>
      <span
        role="progressbar"
        aria-valuenow={a.percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label="تقدّم البرنامج"
        className="h-2 overflow-hidden rounded-full bg-[var(--surface-2)]"
      >
        <span className="block h-full bg-[var(--gold)]" style={{ width: `${a.percent}%` }} />
      </span>
      <p dir="ltr" className="text-xs text-[var(--ink-soft)]">
        {a.percent}% — بقي {Math.max(0, Math.round((a.total - a.done) / 15))} صفحة
        {a.daysLeft !== null ? ` · نحو ${a.daysLeft} يومًا` : ''}
        {a.debt > 0 ? ` · متأخّر ${Math.round(a.debt / 15)}` : ''}
        {a.ahead > 0 ? ` · متقدّم ${Math.round(a.ahead / 15)}` : ''}
      </p>

      <ActivityStrip pages={pages} now={now} />
    </section>
  )
}

/**
 * NAME THE STRIP «نشاط آخر 30 يومًا», NOT «الالتزام» (§12).
 *
 * We store the day a page was LAST READ, not a per-day record of whether the ward was
 * finished; labelling it adherence would be claiming a number we do not have.
 */
function ActivityStrip({
  pages,
  now,
}: {
  pages: Record<number, { readOn: number | null }>
  now: number
}) {
  const today = dayStamp(now)
  const counts = new Map<number, number>()
  for (const p of Object.values(pages)) {
    if (p.readOn === null) continue
    counts.set(p.readOn, (counts.get(p.readOn) ?? 0) + 1)
  }

  return (
    <div className="grid gap-1">
      <span className="text-[11px] text-[var(--ink-faint)]">نشاط آخر 30 يومًا</span>
      {/**
       * `min-w-2` + a scroller, not bare `flex-1`.
       *
       * Thirty `flex-1` cells inside a phone-width card are ~8px each, which is below
       * the width of their own 2px gap and reads as a texture rather than thirty days.
       * A floor of 8px with `overflow-x-auto` keeps every day legible and lets the strip
       * scroll on the narrowest screens instead of dissolving.
       *
       * `title` is a POINTER-ONLY affordance — there is no hover on touch — so the
       * reading also goes out as text for assistive tech and for touch users.
       */}
      <div dir="ltr" className="flex gap-0.5 overflow-x-auto">
        {Array.from({ length: 30 }, (_, i) => {
          const day = today - 29 + i
          const n = counts.get(day) ?? 0
          return (
            <span
              key={day}
              title={`${new Date(day * DAY_MS).toISOString().slice(0, 10)} — ${n}`}
              className="h-5 min-w-2 flex-1 rounded-sm"
              style={{
                background:
                  n === 0
                    ? 'var(--surface-2)'
                    : `color-mix(in srgb, var(--gold) ${Math.min(100, 25 + n * 15)}%, transparent)`,
              }}
            />
          )
        })}
      </div>
      {/* Counted over the WINDOW, not over `counts` — that map holds every day the
          library has ever recorded, so its `size` would include days off this strip. */}
      <span className="sr-only">
        {`قرأت في ${
          Array.from({ length: 30 }, (_, i) => counts.get(today - 29 + i) ?? 0).filter((n) => n > 0)
            .length
        } يومًا من آخر ثلاثين يومًا`}
      </span>
    </div>
  )
}

function DueList({ due }: { due: ReturnType<typeof dueCards> }) {
  const gradePage = useLibrary((s) => s.gradePage)
  const navigate = useNavigate()

  return (
    <section className="grid gap-2 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4">
      <SectionTitle>المراجعة اليوم</SectionTitle>
      {due.length === 0 ? (
        <p className="text-xs text-[var(--ink-faint)]">
          لا مراجعات مستحقّة. علّم صفحة بأنّك حفظتها لتدخل جدول المراجعة.
        </p>
      ) : (
        <ul className="grid gap-1">
          {due.map((card) => (
            <li key={card.page} className="flex flex-wrap items-center gap-2 rounded-xl p-1">
              <Button
                tone="ghost"
                className="text-xs"
                onClick={() =>
                  void navigate({ to: '/read/$page', params: { page: String(card.page) } })
                }
              >
                صفحة {card.page}
              </Button>
              <Chip tone="gold">{TIER_LABELS[tierOf(card)]}</Chip>
              {card.lapses > 0 && <Chip tone="faint">تعثّر {card.lapses}×</Chip>}
              <span className="flex-1" />
              {GRADE_QUALITIES.map((q) => (
                <Button
                  key={q}
                  tone="soft"
                  className="px-2 py-1 text-[11px]"
                  title={GRADE_COPY[q].hint}
                  onClick={() => void gradePage(card.page, q, Date.now())}
                >
                  {GRADE_COPY[q].label}
                </Button>
              ))}
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}

/**
 * The 604-cell grid, CUT INTO THE 30 JUZ (§12).
 *
 * As one undivided wrap it was a texture, not a map: with no landmarks there is no
 * way to tell which part of the mushaf a gap is in, and the row breaks fall wherever
 * the container happens to end.
 *
 * Inside a block a FIXED 11-COLUMN GRID, not a wrap — a juz is 20 or 21 pages, so
 * every block lands on two even rows instead of a long row and a two-cell orphan
 * whose length changes with the card width.
 */
function JuzGrid({
  meta,
  pages,
}: {
  meta: Meta | undefined
  pages: Record<number, { read: boolean; memorised: boolean }>
}) {
  const navigate = useNavigate()
  if (!meta) return null

  return (
    <section className="grid gap-3 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4">
      <div className="flex flex-wrap items-center gap-3">
        <SectionTitle>المصحف كاملًا</SectionTitle>
        <span className="flex items-center gap-2 text-[11px] text-[var(--ink-faint)]">
          <Legend colour="var(--surface-2)" label="لم تُقرأ" />
          <Legend colour="var(--gold-soft)" label="مقروءة" />
          <Legend colour="var(--gold)" label="محفوظة" />
        </span>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {Array.from({ length: 30 }, (_, i) => i + 1).map((juz) => {
          const [from, to] = juzPageRange(meta, juz)
          return (
            <div key={juz} className="grid gap-1 rounded-xl border border-[var(--line)] p-2">
              <div className="flex items-baseline gap-2">
                <span className="text-[11px] font-semibold">الجزء {juz}</span>
                {/* L22: juz 1's page range must render 1–21, never 21–1 (T63). */}
                <span dir="ltr" className="text-[10px] text-[var(--ink-faint)]">
                  {from}–{to}
                </span>
              </div>
              <div className="grid grid-cols-11 gap-0.5">
                {Array.from({ length: to - from + 1 }, (_, k) => from + k).map((page) => {
                  const state = pages[page]
                  const colour = state?.memorised
                    ? 'var(--gold)'
                    : state?.read
                      ? 'var(--gold-soft)'
                      : 'var(--surface-2)'
                  return (
                    <button
                      key={page}
                      type="button"
                      aria-label={`صفحة ${page}`}
                      title={`صفحة ${page}`}
                      className="aspect-square rounded-[3px]"
                      style={{ background: colour }}
                      onClick={() =>
                        void navigate({ to: '/read/$page', params: { page: String(page) } })
                      }
                    />
                  )
                })}
              </div>
            </div>
          )
        })}
      </div>
    </section>
  )
}

function Legend({ colour, label }: { colour: string; label: string }) {
  return (
    <span className="flex items-center gap-1">
      <span className="h-2.5 w-2.5 rounded-[3px]" style={{ background: colour }} />
      {label}
    </span>
  )
}

function Backup() {
  const snapshot = useLibrary((s) => s.snapshot)
  const replaceFromSnapshot = useLibrary((s) => s.replaceFromSnapshot)
  const fileRef = useRef<HTMLInputElement | null>(null)
  const [status, setStatus] = useState<{ ok: boolean; message: string; issues: string[] } | null>(
    null,
  )

  const onExport = () => {
    // A real timestamp for the filename — not the render-scoped `useNow`, which is a
    // hook and cannot be called from an event handler.
    const now = Date.now()
    const snap = snapshot()
    const payload = {
      ...snap,
      // The backup format keys SRS cards by page; the in-memory snapshot is a list.
      srs: Object.fromEntries(snap.srs.map((c) => [String(c.page), c])),
    }
    const blob = new Blob([serialiseBackup(payload, now)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = backupFilename(now)
    a.click()
    URL.revokeObjectURL(url)
  }

  const onImport = async (file: File) => {
    const result = parseBackup(await file.text())
    if (!result.ok) {
      // NOTHING IS CHANGED on a failed import (T59).
      setStatus({
        ok: false,
        message: IMPORT_FAILURE_MESSAGES[result.reason],
        issues: result.issues,
      })
      return
    }
    await replaceFromSnapshot({
      notes: result.backup.notes,
      bookmarks: result.backup.bookmarks,
      srs: Object.values(result.backup.srs),
      pages: result.backup.pages,
      programs: result.backup.programs,
      activeProgram: result.backup.activeProgram,
    })
    setStatus({ ok: true, message: importSuccessMessage(result.backup), issues: [] })
  }

  return (
    <section className="grid gap-2 rounded-2xl border border-[var(--line)] bg-[var(--surface)] p-4">
      <SectionTitle>نسخة احتياطية</SectionTitle>
      <p className="text-xs leading-6 text-[var(--ink-faint)]">
        كل بياناتك محفوظة على هذا الجهاز وحده. صدّر ملفًا لنقلها إلى جهاز آخر.
      </p>
      <div className="flex flex-wrap gap-2">
        <Button tone="soft" className="text-xs" onClick={onExport}>
          <IconDownload size={14} />
          تصدير
        </Button>
        <Button tone="soft" className="text-xs" onClick={() => fileRef.current?.click()}>
          <IconUpload size={14} />
          استيراد
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="application/json,.json"
          className="sr-only"
          aria-label="ملف النسخة الاحتياطية"
          onChange={(e) => {
            const file = e.target.files?.[0]
            if (file) void onImport(file)
            e.target.value = ''
          }}
        />
      </div>
      <p className="text-[11px] text-[var(--gold)]">الاستيراد يستبدل كل البيانات الحالية.</p>
      {status && (
        <div aria-live="polite" className="grid gap-1 rounded-xl bg-[var(--surface-2)] p-2">
          <p className="text-xs">{status.message}</p>
          {status.issues.length > 0 && (
            <ul dir="ltr" className="grid gap-0.5 text-[10px] text-[var(--ink-faint)]">
              {status.issues.slice(0, 8).map((issue, i) => (
                <li key={i}>{issue}</li>
              ))}
            </ul>
          )}
        </div>
      )}
    </section>
  )
}

/**
 * Days on which at least one page was read.
 *
 * TODAY NOT YET BEING READ DOES NOT BREAK THE STREAK — it is still live until the day
 * ends. Anchor on `last === today || last === today - 1`; otherwise the counter
 * collapses to zero every morning.
 */
function computeStreak(
  pages: { readOn: number | null }[],
  now: number,
): { current: number; longest: number } {
  const days = [...new Set(pages.map((p) => p.readOn).filter((d): d is number => d !== null))].sort(
    (a, b) => a - b,
  )
  if (days.length === 0) return { current: 0, longest: 0 }

  let longest = 1
  let run = 1
  for (let i = 1; i < days.length; i += 1) {
    run = days[i] === days[i - 1]! + 1 ? run + 1 : 1
    longest = Math.max(longest, run)
  }

  const today = dayStamp(now)
  const last = days[days.length - 1]!
  if (last !== today && last !== today - 1) return { current: 0, longest }

  let current = 1
  for (let i = days.length - 1; i > 0; i -= 1) {
    if (days[i] === days[i - 1]! + 1) current += 1
    else break
  }
  return { current, longest }
}
